import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanySettings } from '@/contexts/CompanySettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, Lock, User } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { settings } = useCompanySettings();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(username, password, rememberMe);
    
    if (result.success) {
      toast.success('Welcome to MANHA DIGITAL!');
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Login failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex dark">
      {/* Left Side - Image with Overlay */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url(${settings.login_bg_url || 'https://images.pexels.com/photos/10410074/pexels-photo-10410074.jpeg'})`
        }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"></div>
        <div className="relative z-10 flex flex-col justify-center items-start p-16 text-white">
          <div className="mb-8">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt="Logo" className="h-16 mb-6 object-contain" />
            ) : (
              <div className="w-16 h-16 bg-amber-500 rounded-sm mb-6"></div>
            )}
          </div>
          <h1 className="text-5xl font-bold mb-4 tracking-tighter" data-testid="login-company-name">
            {settings.company_name || 'MANHA DIGITAL'}
          </h1>
          <p className="text-2xl text-amber-500 mb-8" data-testid="login-subtitle">
            {settings.subtitle || 'Your Dreams, Our Deals'}
          </p>
          <div className="mt-auto text-sm text-slate-400">
            Developed by Manha Digital Team
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-950">
        <div className="w-full max-w-md">
          {/* Glass Card */}
          <div
            className="backdrop-blur-xl bg-black/40 border border-white/20 rounded-sm p-8 shadow-2xl"
            data-testid="login-card"
          >
            {/* Mobile Logo */}
            <div className="lg:hidden mb-8 text-center">
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="Logo" className="mx-auto h-12 mb-4 object-contain" />
              ) : (
                <div className="w-12 h-12 bg-amber-500 rounded-sm mx-auto mb-4"></div>
              )}
              <h2 className="text-2xl font-bold text-white" data-testid="mobile-company-name">
                {settings.company_name || 'MANHA DIGITAL'}
              </h2>
              <p className="text-sm text-amber-500" data-testid="mobile-subtitle">
                {settings.subtitle || 'Your Dreams, Our Deals'}
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-white mb-6">
              Sign In
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-300">
                  Username
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="username"
                    data-testid="login-username-input"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    required
                    className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="pl-10 pr-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500"
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <button
                  type="button"
                  className="text-sm text-amber-500 hover:text-amber-400 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-6 rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}